import type { CabinetMfaStatus, MfaMethod } from "@planerius/contracts/cabinet-auth"

import { plural } from "../../lib/plural"
import type { BadgeTone } from "../../ui"

/**
 * Второй фактор на экране безопасности (1-Э1.10, 5-М7) — без разметки.
 *
 * Здесь решается всё, что можно решить словами: что написано на плашке, можно ли выключить
 * фактор и почему нет, сколько осталось резервных кодов и когда о них пора напомнить.
 * Правила такого рода ломаются молча, поэтому они проверяются тестами отдельно от вёрстки.
 */

/** Сколько кодов считается «на исходе»: об этом надо сказать до того, как они кончатся. */
export const RECOVERY_LOW = 3

export interface MfaView {
  readonly badgeText: string
  readonly badgeTone: BadgeTone
  /** Строка под заголовком: кто требует фактор или что он не обязателен. */
  readonly explain: string
  readonly enableLabel: string
  /**
   * Причина, по которой выключить нельзя. undefined — можно.
   *
   * Строка уходит в deniedReason кнопки, то есть человек видит её ДО нажатия, а не
   * получает отказом после. Кнопка, которая отвечает «нельзя», хуже выключенной кнопки
   * с объяснением.
   */
  readonly disableDenied: string | undefined
  /** Сколько резервных кодов осталось. null — фактор выключен, кодов не бывает. */
  readonly recoveryLine: string | null
  readonly recoveryLow: boolean
  /**
   * Начатая и неподтверждённая настройка: QR уже показан.
   *
   * Отдельным признаком, потому что продолжать её надо ВВОДОМ КОДА, а не новым QR.
   * Кнопка «Продолжить настройку» раньше выпускала новый секрет и гасила прежний —
   * то есть человек, уже отсканировавший код, вводил цифры из приложения, которое
   * к этому моменту перестало подходить, и читал «код не подошёл», сделав всё верно.
   */
  readonly pending: boolean
}

export function mfaView(status: CabinetMfaStatus): MfaView {
  if (!status.enabled) {
    return {
      badgeText: "выключен",
      badgeTone: "gray",
      explain: status.required
        ? // Требование есть, фактора нет. Такое состояние возможно у человека, которому
          // требование включили ПОСЛЕ его последнего входа: в кабинете он ещё сидит
          // по старой сессии, а следующий вход попросит завести фактор.
          `Обязателен: ${status.requiredBy ?? "правило салона"}. Заведите его сейчас — иначе следующий вход попросит это сделать.`
        : "Для вашей роли не обязателен, но с ним украденный пароль не даёт войти.",
      enableLabel: status.pending ? "Продолжить настройку" : "Включить",
      disableDenied: "Фактор и так выключен",
      recoveryLine: null,
      recoveryLow: false,
      pending: status.pending,
    }
  }
  const left = status.recoveryLeft
  return {
    badgeText: "включён",
    badgeTone: "ok",
    explain: status.required
      ? `Обязателен: ${status.requiredBy ?? "правило салона"}.`
      : "Включён по вашему решению. Выключить можно кодом из приложения.",
    enableLabel: "Подключить другое приложение",
    disableDenied: status.required
      ? `Обязателен: ${status.requiredBy ?? "правило салона"}. Можно перевыпустить коды или подключить другое приложение.`
      : undefined,
    recoveryLine:
      left === 0
        ? "Резервных кодов не осталось. Перевыпустите — без них потерянный телефон закроет вход насовсем."
        : `${plural(left, "Остался", "Осталось", "Осталось")} ${String(left)} ${plural(left, "резервный код", "резервных кода", "резервных кодов")}.`,
    recoveryLow: left <= RECOVERY_LOW,
    pending: status.pending,
  }
}

/**
 * Проверка кода ДО отправки.
 *
 * Смысл не в экономии запроса: неудачный код сервер считает попыткой входа и приближает
 * блокировку учётки. Человек, набравший пять цифр вместо шести, не должен расходовать
 * на это попытку.
 */
export function codeError(code: string, method: MfaMethod): string | null {
  const value = code.trim()
  if (value.length === 0) return "Введите код"
  if (method === "totp") {
    const digits = value.replace(/\D/gu, "")
    if (digits.length !== 6) return "Код из приложения — шесть цифр"
    return null
  }
  // Резервный код: буквы и цифры, дефис человек может и не вводить.
  if (value.replace(/[\s-]/gu, "").length < 8) return "Резервный код короче не бывает"
  return null
}

/**
 * Секрет для ввода руками — группами по четыре.
 *
 * Камеры есть не у всех и не всегда: администратор за стойкой заводит фактор на телефоне,
 * который лежит в другой руке, и вводит секрет вручную. Тридцать два знака подряд при этом
 * читаются с ошибкой, а восемь групп по четыре — нет.
 */
export function secretGroups(secret: string): readonly string[] {
  const groups: string[] = []
  for (let i = 0; i < secret.length; i += 4) {
    groups.push(secret.slice(i, i + 4))
  }
  return groups
}

/**
 * Коды восстановления одним текстом — для «Скопировать» и для файла.
 *
 * С заголовком и датой: листок, найденный через год в ящике стола, должен сам объяснять,
 * что это и к чему. Без подписи такой список выглядит как мусор и выбрасывается.
 */
export function recoveryCodesText(codes: readonly string[], now: Date): string {
  const date = now.toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" })
  return [
    "Резервные коды Planerius",
    `выпущены ${date}`,
    "",
    "Каждый код срабатывает один раз. Храните их не там, где телефон с приложением.",
    "",
    ...codes,
    "",
  ].join("\n")
}

/** Имя файла с кодами: без даты человек не поймёт, какой из двух файлов свежий. */
export function recoveryCodesFileName(now: Date): string {
  const iso = `${String(now.getFullYear())}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`
  return `planerius-резервные-коды-${iso}.txt`
}
